import React, { useState, useEffect } from 'react';
import { format, addMonths, startOfYear, getWeek } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Calendar,
  BarChart3,
  PieChart,
  Coins,
  LogOut,
  FileText,
  ChevronDown,
  ChevronRight,
  Calendar as CalendarIcon,
  LayoutDashboard,
  Settings as SettingsIcon,
  Car,
  Sun,
  MessageSquare,
  Euro,
  FileText as FileTextIcon,
  ArrowLeftRight,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import ProfilePhoto from './ProfilePhoto';
import { useAccentColor } from '../lib/useAccentColor';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onMonthSelect: (date: Date) => void;
  onWeekSelect: (year: number, week: number) => void;
  onQuarterSelect: (year: number, quarter: number) => void;
  onTotalOverviewSelect: () => void;
  onReportsSelect: () => void;
  onDayOverviewSelect: () => void;
  onSettingsSelect: () => void;
  onSignOut: () => void;
}

export default function Sidebar({
  isOpen,
  onClose,
  onMonthSelect,
  onWeekSelect,
  onQuarterSelect,
  onTotalOverviewSelect,
  onReportsSelect,
  onDayOverviewSelect,
  onSettingsSelect,
  onSignOut,
}: SidebarProps) {
  const { accentColor } = useAccentColor();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedQuarter, setSelectedQuarter] = useState<{[key: number]: number}>({});
  const [expandedYear, setExpandedYear] = useState<number | null>(null);
  const [expandedOverviewsYear, setExpandedOverviewsYear] = useState<number | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [activeYears, setActiveYears] = useState<number[]>([]);
  const [clickCount, setClickCount] = useState(0);
  const [showHiddenLink, setShowHiddenLink] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showFixedExpenses, setShowFixedExpenses] = useState(true);
  const [showTips, setShowTips] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: settings } = await supabase
        .from('settings')
        .select('company_name, active_years, profile_photo_url, show_fixed_expenses, show_tips')
        .eq('user_id', user.id)
        .maybeSingle();

      if (settings) {
        setCompanyName(settings.company_name);
        setProfilePhotoUrl(settings.profile_photo_url);
        setShowFixedExpenses(settings.show_fixed_expenses ?? true);
        setShowTips(settings.show_tips ?? false);

        if (settings.active_years) {
          const years = settings.active_years.sort((a: number, b: number) => a - b);
          setActiveYears(years);

          // Initialize selectedQuarter state for each year
          const initialQuarters = years.reduce((acc: {[key: number]: number}, year: number) => {
            acc[year] = 0;
            return acc;
          }, {});
          setSelectedQuarter(initialQuarters);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePhotoUpdate = (url: string) => {
    setProfilePhotoUrl(url);
  };

  const yearSections = React.useMemo(() => {
    return activeYears.map(year => {
      const yearStart = startOfYear(new Date(year, 0, 1));
      const allMonths = Array.from({ length: 12 }, (_, i) => addMonths(yearStart, i));
      
      return {
        year,
        quarters: Array.from({ length: 4 }, (_, i) => ({
          name: `K${i + 1}`,
          months: allMonths.slice(i * 3, (i + 1) * 3)
        }))
      };
    });
  }, [activeYears]);

  const currentDate = new Date();
  const currentMonthPath = `/month/${format(currentDate, 'yyyy/MM')}`;

  const handleMonthSelect = React.useCallback((date: Date, type: 'revenue' | 'hours' = 'revenue') => {
    const monthPath = type === 'revenue' 
      ? `/month/${format(date, 'yyyy/MM')}`
      : `/hours/${format(date, 'yyyy/MM')}`;
      
    navigate(monthPath);
    onClose();
  }, [location.pathname, navigate, onClose]);

  // Mobile sidebar classes
  const mobileSidebarClasses = `
    fixed inset-y-0 left-0 z-50 w-64
    bg-gray-900 border-r border-gray-800
    transform ${isOpen ? 'translate-x-0' : '-translate-x-full'}
    transition-transform duration-300 ease-in-out
    lg:hidden
    overflow-y-auto
    pb-safe-area-bottom
  `;

  // Desktop sidebar classes
  const desktopSidebarClasses = `
    hidden lg:block w-64 flex-shrink-0 fixed top-0 bottom-0 left-0
    bg-gray-900
    border-r border-gray-800
  `;

  const getMenuItemClasses = (path: string) => {
    const isActive = location.pathname === path;
    return `w-full flex items-center px-3 py-2 text-sm ${
      isActive
        ? 'text-white bg-accent font-medium'
        : 'text-gray-400 hover:text-white hover:bg-gray-800'
    } rounded-lg transition-all duration-200`;
  };

  const menuHeaderClasses = "text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2 px-3";

  // Show a placeholder while loading
  if (loading) {
    return (
      <>
        <div className={mobileSidebarClasses}>
          <div className="h-full flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <Car className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                </div>
                <div>
                  <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded mt-2"></div>
                </div>
              </div>
            </div>
            <div className="flex-1 p-4">
              <div className="space-y-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="space-y-2">
                      {[...Array(4)].map((_, j) => (
                        <div key={j} className="h-8 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className={desktopSidebarClasses}>
          <div className="h-full flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <Car className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                </div>
                <div>
                  <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded mt-2"></div>
                </div>
              </div>
            </div>
            <div className="flex-1 p-4">
              <div className="space-y-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="space-y-2">
                      {[...Array(4)].map((_, j) => (
                        <div key={j} className="h-8 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const sidebarContent = (
    <div className="h-full flex flex-col">
      {/* Company Logo and Name */}
      <div className="p-4 border-b border-gray-800 lg:block hidden">
        <div className="flex items-center space-x-3">
          <ProfilePhoto
            url={profilePhotoUrl}
            size="md"
            onUpdate={handleProfilePhotoUpdate}
            editable
          />
          <div className="cursor-pointer"
            onClick={() => {
              setClickCount(prev => {
                const newCount = prev + 1;
                if (newCount === 5) {
                  setShowHiddenLink(true);
                  return 0;
                }
                return newCount;
              });
            }}
          >
            <h1 className="text-base font-semibold text-white">
              {companyName === null ? '' : companyName}
            </h1>
            <p className="text-xs font-medium text-gray-500">Administratie</p>
            {showHiddenLink && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/hidden');
                  onClose();
                }}
                className="text-xs text-gray-500 hover:text-gray-400 mt-1"
              >
                Administratie
              </button>
            )}
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-4 pb-24">
        <div className="space-y-6">
          {/* Overzichten Dropdown */}
          <div>
            <h3 className={menuHeaderClasses}>
              Overzichten
            </h3>
            <div className="space-y-2">
              <button
                onClick={onTotalOverviewSelect}
                className={getMenuItemClasses('/total')}
              >
                <LayoutDashboard className="h-5 w-5 mr-3" />
                <span>Dashboard</span>
              </button>
              
              {/* Overzichten Dropdown */}
              <div className="rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedOverviewsYear(expandedOverviewsYear === currentDate.getFullYear() ? null : currentDate.getFullYear())}
                  className={`w-full flex items-center px-3 py-2 text-sm transition-all duration-200 rounded-lg ${
                    expandedOverviewsYear === currentDate.getFullYear()
                      ? 'bg-gray-800 text-white font-medium'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                   <BarChart3 className="h-4 w-4 mr-2.5" />
                  <span className="flex-1 text-left">Overzichten</span>
                  {expandedOverviewsYear === currentDate.getFullYear() ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                </button>

                {expandedOverviewsYear === currentDate.getFullYear() && (
                  <div className="mt-1 pl-6 space-y-0.5">
                    <button
                      onClick={onDayOverviewSelect}
                      className={`w-full flex items-center px-3 py-1.5 text-xs transition-all duration-200 rounded-lg ${
                        location.pathname === '/days'
                          ? 'text-white bg-gray-800 font-medium'
                          : 'text-gray-500 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      <Sun className="h-3.5 w-3.5 mr-2" />
                      <span>Dagoverzicht</span>
                    </button>

                    <button
                      onClick={() => onWeekSelect(currentDate.getFullYear(), getWeek(currentDate, { weekStartsOn: 1 }))}
                      className={`w-full flex items-center px-3 py-1.5 text-xs transition-all duration-200 rounded-lg ${
                        location.pathname.includes('/weeks/')
                          ? 'text-white bg-gray-800 font-medium'
                          : 'text-gray-500 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      <BarChart3 className="h-3.5 w-3.5 mr-2" />
                      <span>Weekoverzicht</span>
                    </button>

                    <button
                      onClick={() => {
                        navigate('/months');
                        onClose();
                      }}
                      className={`w-full flex items-center px-3 py-1.5 text-xs transition-all duration-200 rounded-lg ${
                        location.pathname === '/months'
                          ? 'text-white bg-gray-800 font-medium'
                          : 'text-gray-500 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      <Calendar className="h-3.5 w-3.5 mr-2" />
                      <span>Maandoverzicht</span>
                    </button>

                    <button
                      onClick={() => onQuarterSelect(currentDate.getFullYear(), Math.floor(currentDate.getMonth() / 3) + 1)}
                      className={`w-full flex items-center px-3 py-1.5 text-xs transition-all duration-200 rounded-lg ${
                        location.pathname.includes('/quarters/')
                          ? 'text-white bg-gray-800 font-medium'
                          : 'text-gray-500 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      <PieChart className="h-3.5 w-3.5 mr-2" />
                      <span>Kwartaaloverzicht</span>
                    </button>

                    <button
                      onClick={() => { navigate('/compare'); onClose(); }}
                      className={`w-full flex items-center px-3 py-1.5 text-xs transition-all duration-200 rounded-lg ${
                        location.pathname === '/compare'
                          ? 'text-white bg-gray-800 font-medium'
                          : 'text-gray-500 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      <ArrowLeftRight className="h-3.5 w-3.5 mr-2" />
                      <span>Vergelijken</span>
                    </button>
                  </div>
                )}
              </div>
              
              {/* Current Month - Now as third item under Overzichten */}
              {activeYears.includes(currentDate.getFullYear()) && (
                <button
                  onClick={() => handleMonthSelect(currentDate)}
                  className={`w-full flex items-center px-3 py-2 text-sm ${
                    location.pathname === currentMonthPath
                      ? 'bg-green-500/10 text-green-700 dark:bg-green-400/10 dark:text-green-400 font-medium'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  } rounded-lg transition-all duration-200`}
                >
                  <Calendar className="h-4 w-4 mr-2.5" />
                  <span className="flex-1 text-left">
                    Huidige maand
                    <span className="block text-[10px] opacity-75 font-normal">
                      {format(currentDate, 'MMMM yyyy', { locale: nl })}
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Omzet */}
          <div>
            <h3 className={menuHeaderClasses}>
              Omzet
            </h3>
            <div className="space-y-2">
              {/* Year Sections */}
              {yearSections.map((yearSection) => (
                <div key={yearSection.year} className="rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedYear(expandedYear === yearSection.year ? null : yearSection.year)}
                    className={`w-full flex items-center px-3 py-2 text-sm ${
                      expandedYear === yearSection.year
                        ? 'bg-gray-800 text-white font-medium'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    } rounded-lg transition-all duration-200`}
                  >
                    <Calendar className="h-4 w-4 mr-2.5" />
                    <span className="flex-1 text-left">Omzet {yearSection.year}</span>
                    {expandedYear === yearSection.year ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                  </button>

                  {expandedYear === yearSection.year && (
                    <div className="mt-1 rounded-lg bg-gray-800 p-2">
                      {/* Quarter Tabs */}
                      <div className="flex space-x-1 mb-2">
                        {yearSection.quarters.map((quarter, index) => (
                          <button
                            key={quarter.name}
                            onClick={() => setSelectedQuarter({ ...selectedQuarter, [yearSection.year]: index })}
                            className={`flex-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                              selectedQuarter[yearSection.year] === index
                                ? 'bg-accent text-white'
                                : 'text-gray-500 hover:bg-gray-700 hover:text-white'
                            }`}
                          >
                            {quarter.name}
                          </button>
                        ))}
                      </div>

                      {/* Month Grid */}
                      <div className="grid grid-cols-3 gap-1">
                        {yearSection.quarters[selectedQuarter[yearSection.year] || 0].months.map((date) => {
                          const monthPath = `/month/${format(date, 'yyyy/MM')}`;
                          const isActive = location.pathname === monthPath;
                          const isCurrentMonth = format(date, 'yyyy-MM') === format(currentDate, 'yyyy-MM');

                          return (
                            <button
                              key={format(date, 'MM')}
                              onClick={() => handleMonthSelect(date)}
                              className={`px-2 py-1.5 text-xs font-medium rounded transition-colors ${
                                isActive
                                  ? isCurrentMonth
                                    ? 'bg-green-500/10 text-green-700 dark:bg-green-400/10 dark:text-green-400'
                                    : 'bg-accent text-white'
                                  : 'bg-gray-900 text-gray-400 hover:bg-gray-700 hover:text-white border border-gray-700'
                              }`}
                            >
                              {format(date, 'MMM', { locale: nl })}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Fooi - Only show if enabled */}
              {showTips && (
                <button
                  onClick={() => {
                    navigate('/hidden');
                    onClose();
                  }}
                  className={getMenuItemClasses('/hidden')}
                >
                  <Coins className="h-5 w-5 mr-3" />
                  <span>Fooi</span>
                </button>
              )}
            </div>
          </div>

          {/* Administratie */}
          <div>
            <h3 className={menuHeaderClasses}>
              Administratie
            </h3>
            <div className="space-y-2">
              {/* Vaste Lasten - Only show if enabled */}
              {showFixedExpenses && (
                <button
                  onClick={() => {
                    navigate('/expenses');
                    onClose();
                  }}
                  className={getMenuItemClasses('/expenses')}
                >
                  <Euro className="h-5 w-5 mr-3" />
                  <span>Vaste Lasten</span>
                </button>
              )}

              {/* Rapportages */}
              <button
                onClick={onReportsSelect}
                className={getMenuItemClasses('/reports')}
              >
                <FileTextIcon className="h-5 w-5 mr-3" />
                <span>Omzetrapport</span>
              </button>

            </div>
          </div>

          <div>
            <h3 className={menuHeaderClasses}>
              Systeem
            </h3>
            <ul className="space-y-1">
              <li>
                <button
                  onClick={onSettingsSelect}
                  className={getMenuItemClasses('/settings')}
                >
                  <SettingsIcon className="h-5 w-5 mr-3" />
                  <span>Instellingen</span>
                </button>
              </li>
              <li>
                <button
                  onClick={onSignOut}
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-all duration-200"
                >
                  <LogOut className="h-4 w-4 mr-2.5" />
                  <span>Uitloggen</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </div>
  );

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden pb-safe-area-bottom ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        } transition-opacity duration-300`}
        onClick={onClose}
      />

      {/* Mobile sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64
        bg-gray-900 border-r border-gray-800
        transform ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        transition-transform duration-300 ease-in-out
        lg:hidden
        overflow-y-auto
        pb-safe-area-bottom
      `}>
        {sidebarContent}
      </div>

      {/* Desktop sidebar */}
      <aside className={`${desktopSidebarClasses} overflow-y-auto`}>
        {sidebarContent}
      </aside>
    </>
  );
}